import React, { useState, useEffect, useRef } from 'react';
import { Button, Dimmer, Icon, Loader } from 'semantic-ui-react';
import { AddButton } from '../../components/AddButton/AddButton';
import { Grid, GridColumn as Column } from '@progress/kendo-react-grid';
import { SemanticToastContainer, toast } from 'react-semantic-toasts';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import { useUtilityMeters } from '../../providers/UtilityMeterProvider';
import { UtilityMeterModal } from './UtilityMeterModals/UtilityMeterModal';
import { DeleteConfirmation } from '../UtilityAccounts/UtilityAccountModals/DeleteConfirmation';
import { parseUtilityMeterCsv, SUPPORTED_METER_TYPES, unsupportedMeterTypeError } from './utilityMeterCsv';
import { BULK_UPLOAD_TIMEOUT_MS } from '../../services/UtilityMeterService';
import './UtilityMeters.css';

const ERROR_DETAIL_MAX_LENGTH = 300;

// Renders the server's 400 detail (plain text or JSON) into a toast-sized message.
const formatUploadError = error => {
    if (error?.code === 'ECONNABORTED') {
        return `The upload timed out after ${BULK_UPLOAD_TIMEOUT_MS / 60000} minutes. The data may still be processing — check the meter before re-uploading.`;
    }
    const detail = error?.response?.data;
    let message = null;
    if (typeof detail === 'string' && detail.trim() !== '') {
        message = detail.trim();
    } else if (detail && typeof detail === 'object') {
        try {
            message = JSON.stringify(detail);
        } catch {
            message = null;
        }
    }
    if (!message) {
        message = 'An error occurred while uploading meter data.';
    }
    return message.length > ERROR_DETAIL_MAX_LENGTH
        ? `${message.slice(0, ERROR_DETAIL_MAX_LENGTH)}…`
        : message;
};

// Combined client + server skip wording for the success toast.
const formatSkippedDetail = (clientSkipped, serverSkipped) => {
    const total = clientSkipped + serverSkipped;
    if (total === 0) return '';
    let detail = ` ${total} ${total === 1 ? 'row' : 'rows'} skipped`;
    if (clientSkipped > 0 && serverSkipped > 0) {
        detail += ` (${clientSkipped} in the file, ${serverSkipped} by the server)`;
    }
    return `${detail}.`;
};

export const UtilityMeters = () => {
    const { utilityAccountId: utilityAccountIdParam } = useParams();
    const utilityAccountId = parseInt(utilityAccountIdParam, 10);
    const { state } = useLocation();
    const navigate = useNavigate();
    const accountName = state?.accountName ?? `Utility Account #${utilityAccountId}`;

    const [loading, setLoading] = useState(false);
    const [modalOpen, setModalOpen] = useState(false);
    const [modalType, setModalType] = useState('Create');
    const [selectedMeter, setSelectedMeter] = useState(null);
    const [deleteOpen, setDeleteOpen] = useState(false);
    const [uploadingMeterId, setUploadingMeterId] = useState(null);

    const fileInputRef = useRef(null);
    const uploadingMeterRef = useRef(null);
    const isMountedRef = useRef(true);

    const { utilityMeters, getUtilityMeters, deleteUtilityMeter, uploadUtilityMeterData } = useUtilityMeters();

    useEffect(() => {
        isMountedRef.current = true;
        return () => { isMountedRef.current = false; };
    }, []);

    useEffect(() => {
        if (!uploadingMeterId) return;
        const handler = e => { e.preventDefault(); };
        window.addEventListener('beforeunload', handler);
        return () => window.removeEventListener('beforeunload', handler);
    }, [uploadingMeterId]);

    useEffect(() => {
        setLoading(true);
        getUtilityMeters(utilityAccountId)
            .then(() => setLoading(false))
            .catch(() => {
                setLoading(false);
                toast({
                    type: 'error',
                    title: 'Error',
                    description: 'Failed to load utility meters.',
                    animation: 'fade down',
                    time: 8000,
                });
            });
    }, [utilityAccountId, getUtilityMeters]);

    const handleCreate = () => {
        setSelectedMeter(null);
        setModalType('Create');
        setModalOpen(true);
    };

    const handleEdit = meter => {
        setSelectedMeter(meter);
        setModalType('Edit');
        setModalOpen(true);
    };

    const handleDelete = meter => {
        setSelectedMeter(meter);
        setDeleteOpen(true);
    };

    const confirmDelete = async () => {
        setLoading(true);
        try {
            await deleteUtilityMeter(selectedMeter.id);
            await getUtilityMeters(utilityAccountId);
            toast({
                type: 'success',
                title: 'Success',
                description: 'Utility meter deleted successfully',
                animation: 'fade down',
                time: 4000,
            });
        } catch (error) {
            console.error('Error deleting utility meter:', error);
            toast({
                type: 'error',
                title: 'Error',
                description: 'An error occurred while deleting the utility meter.',
                animation: 'fade down',
                time: 8000,
            });
         }
        setDeleteOpen(false);
        setSelectedMeter(null);
        setLoading(false);
    };

    const handleUploadClick = (meter) => {
        if (!SUPPORTED_METER_TYPES.includes((meter.type ?? '').toLowerCase())) {
            uploadingMeterRef.current = null;
            toast({
                type: 'error',
                title: 'Upload Not Supported',
                description: unsupportedMeterTypeError(meter.type),
                animation: 'fade down',
                time: 8000,
            });
            return;
        }
        uploadingMeterRef.current = meter;
        fileInputRef.current.value = '';
        fileInputRef.current.click();
    };

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (!file) return;

        const meter = uploadingMeterRef.current;
        setUploadingMeterId(meter.id);

        const reader = new FileReader();
        reader.onload = async (event) => {
            try {
                // Parse failures carry their own message: format errors (bad
                // header, quoted fields, wrong column count, row cap) and
                // zero-valid-rows files return distinct error strings.
                const parsed = parseUtilityMeterCsv(event.target.result, meter.type);
                if (!parsed.ok) {
                    if (!isMountedRef.current) return;
                    toast({
                        type: 'error',
                        title: 'Upload Failed',
                        description: parsed.error,
                        animation: 'fade down',
                        time: 8000,
                    });
                    return;
                }

                const payload = parsed.rows.map(row => ({
                    meterId: meter.id,
                    timestamp: row.timestamp,
                    values: row.values,
                }));

                const result = await uploadUtilityMeterData(meter.id, payload);
                if (!isMountedRef.current) return;

                // The service returns null when a 401 is swallowed — the
                // upload did NOT happen, so this must never reach the success toast.
                if (!result) {
                    toast({
                        type: 'error',
                        title: 'Upload Failed',
                        description: 'The upload could not be completed — your session may have expired. Please sign in again and retry.',
                        animation: 'fade down',
                        time: 8000,
                    });
                    return;
                }

                const documentsWritten = result.documentsWritten ?? 0;
                const kwhOnlyNote = parsed.kwhOnly ? ' (kwh only — no kvarh values found)' : '';
                const skippedDetail = formatSkippedDetail(parsed.skipped, result.rowsSkipped ?? 0);
                toast({
                    type: 'success',
                    title: 'Upload Complete',
                    description: `${documentsWritten} ${documentsWritten === 1 ? 'timestamp' : 'timestamps'} uploaded${kwhOnlyNote}.${skippedDetail}`,
                    animation: 'fade down',
                    time: 10000,
                });
                if (result.elasticWriteFailed) {
                    toast({
                        type: 'warning',
                        title: 'Search Indexing Failed',
                        description: 'Search indexing failed — data is saved; an admin can reindex.',
                        animation: 'fade down',
                        time: 10000,
                    });
                }
            } catch (error) {
                if (!isMountedRef.current) return;
                toast({
                    type: 'error',
                    title: 'Upload Failed',
                    description: formatUploadError(error),
                    animation: 'fade down',
                    time: 8000,
                });
            } finally {
                if (isMountedRef.current) setUploadingMeterId(null);
            }
        };
        reader.onerror = () => {
            if (!isMountedRef.current) return;
            setUploadingMeterId(null);
            toast({
                type: 'error',
                title: 'Upload Failed',
                description: 'The selected file could not be read. Please try selecting it again.',
                animation: 'fade down',
                time: 8000,
            });
        };
        reader.readAsText(file);
    };

    const refreshMeters = () => getUtilityMeters(utilityAccountId);

    return (
        <>
            <UtilityMeterModal
                open={modalOpen}
                setOpen={setModalOpen}
                type={modalType}
                utilityAccountId={utilityAccountId}
                selectedMeter={selectedMeter}
                onSuccess={refreshMeters}
            />
            <DeleteConfirmation
                open={deleteOpen}
                setOpen={setDeleteOpen}
                submitAction={confirmDelete}
                loading={loading}
                entityName="utility meter"
            />
            <div>
                <div className="pageHeader">
                    <div className="utility-meters-header-left">
                        <Button icon="arrow left" aria-label="Back" onClick={() => navigate(-1)} />
                        <h1>Utility Meters — {accountName}</h1>
                    </div>
                    <AddButton onClick={handleCreate}>Create Meter</AddButton>
                </div>
                <input
                    type="file"
                    accept=".csv"
                    ref={fileInputRef}
                    style={{ display: 'none' }}
                    onChange={handleFileChange}
                    data-testid="meter-data-file-input"
                />
                <div style={{ position: 'relative' }}>
                    <Dimmer active={loading} inverted>
                        <Loader>Loading...</Loader>
                    </Dimmer>
                    <Grid data={utilityMeters}>
                        <Column field="name" title="Name" />
                        <Column field="meterId" title="Meter ID" />
                        <Column field="type" title="Type" />
                        <Column field="units" title="Units" />
                        <Column
                            sortable={false}
                            width={400}
                            cell={props => (
                                <td>
                                    <Button disabled={uploadingMeterId === props.dataItem.id} onClick={() => handleEdit(props.dataItem)} data-testid={`edit-meter-button-${props.dataItem.id}`}><Icon name="pencil" /> Edit</Button>
                                    <Button disabled={uploadingMeterId === props.dataItem.id} onClick={() => handleDelete(props.dataItem)} data-testid={`delete-meter-button-${props.dataItem.id}`}><Icon name="trash" /> Delete</Button>
                                    <Button
                                        loading={uploadingMeterId === props.dataItem.id}
                                        disabled={uploadingMeterId !== null}
                                        onClick={() => handleUploadClick(props.dataItem)}
                                        data-testid={`upload-data-button-${props.dataItem.id}`}
                                    >
                                        <Icon name="upload" /> Upload Data
                                    </Button>
                                </td>
                            )}
                        />
                    </Grid>
                </div>
                <SemanticToastContainer position="bottom-center" />
            </div>
        </>
    );
};
